import {
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  MENU_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
} from '../../../../utils/spds/metadata';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../../layout/flex/column.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import Super from '../../core/base.gjs';
import styles from './base.module.scss';

const METADATA = {
  component: 'Spds::Core::Menu::Base',
  description: `
    Simple, easy to use menu component.

    ### Positioning

    Providing the \`@anchorElement\` argument as a DOM element that is used to compute the \`top\` property for the menu. This can be
    further modified by providing the \`@anchorOffset\` argument, which adjusts the distance between the top of the menu and the bottom of
    the anchor element. Left/right alignment is determined by the width of the menu and the horizontal positioning of the anchor. If the anchor
    position plus the menu width extends beyond the right edge of the viewport, the menu will automatically align to the right side of the anchor.
    Otherwise, the menu defaults to aligning to the left side of the anchor. In either case, the position of the non-aligned side will be determined
    in one of two ways:

    * Default behavior causes the menu to flex its width to fit the container of the anchor element
    * If the \`@fitContent\` argument is provided, the menu will restrict its width to the required space to fit its largest content
  `,
  meta: {
    ...pickArgs(COMPONENT_STATE_ARGS, ['open']).meta,
    ...pickArgs(CONTAINER_ARGS, ['fitContent']).meta,
    ...pickArgs(MENU_ARGS, ['anchorElement']).meta,
    anchorOffset: {
      ...MENU_ARGS.meta.anchorOffset,
      defaultValue: 4,
    },
    animated: {
      ...COMPONENT_STATE_ARGS.meta.animated,
      defaultValue: true,
    },
    column: {
      ...CONTAINER_ARGS.meta.column,
      defaultValue: true,
    },
  },
  name: 'Menu Base',
  resources: {
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/core/menu/base.gjs',
  },
};

export default class SpdsCoreMenuBase extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-menu';
  classesToMapToArgs = ['animated', 'open'];
  classesToMapToAttrs = ['side'];
  styles = styles;

  @tracked menuElement;
  @tracked side = 'left';

  get anchorBoundingRect() {
    return this.args.anchorElement?.getBoundingClientRect();
  }

  get anchorDistance() {
    return (
      this.args.anchorOffset ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'anchorOffset')
    );
  }

  get canBePositioned() {
    return !!this.args.anchorElement && !!this.baseElement;
  }

  get component() {
    return this.isColumn ? FlexColumn : FlexRow;
  }

  get isColumn() {
    return this.args.column ?? getDefaultValueFromMetadata(this.constructor.metadata, 'column');
  }

  get parentBoundingRect() {
    return this.args.anchorElement?.parentElement?.getBoundingClientRect();
  }

  setMenuPosition = modifier(() => {
    if (!this.canBePositioned) return;

    let anchorBottom = this.anchorBoundingRect?.bottom;
    let menuRight = this.anchorBoundingRect?.x + this.baseElement?.getBoundingClientRect()?.width;
    let parentTop = this.parentBoundingRect?.top || 0;
    let top = anchorBottom - parentTop + this.anchorDistance;

    this.side = menuRight > window.innerWidth ? 'right' : 'left';
    this.baseElement.style.top = `${top}px`;
  });

  <template>
    <this.component
      @absolute={{true}}
      @fitContentWidth={{@fitContent}}
      aria-expanded={{@open}}
      class={{this.classes}}
      data-test-spds-menu
      id={{this.id}}
      role="listbox"
      {{this.setupComponent}}
      {{this.setMenuPosition}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield}}
    </this.component>
  </template>
}

export const SPDS_CORE_MENU_BASE_METADATA = SpdsCoreMenuBase.metadata;
